import moment from 'moment-timezone';
import Vue from 'vue';
import SubscriptionConfirmationModal from '@/components/Modals/SubscriptionConfirmationModal.vue';

/**
 * AgreementModal Utility Class
 * Jembatan antara main.js dan SubscriptionConfirmationModal.vue component
 * Render component secara programmatic seperti Swal.fire()
 * 
 * Usage:
 * const result = await AgreementModal.fire({
 *   featureType: 'predict_module',
 *   store: store,
 *   global: global,
 *   userData: userData,
 *   to: to,
 *   from: from
 * });
 * 
 * Result:
 * {
 *   isConfirmed: true/false,
 *   redirectPath: '/path/to/redirect',
 *   selectedModules: [...],
 *   hasAgreement: true/false
 * }
 */
class AgreementModal {
    /**
     * Main method to show agreement modal
     * @param {Object} options - Configuration options
     * @param {string} options.featureType - Feature type (predict_module or clean_module)
     * @param {Object} options.store - Vuex store instance
     * @param {Object} options.global - Global state object
     * @param {Object} options.userData - User data object
     * @param {Object} options.to - Router to object
     * @param {Object} options.from - Router from object
     * @returns {Promise} Promise that resolves with custom result object
     */
    static async fire(options = {}) {
        // console.log('AgreementModal.fire: options', options);
        const { featureType, store, global, userData, to, from, router, multiAgreementsData, pricing: passedPricing } = options;

        // Validate required parameters
        if (!featureType || !store || !global || !userData) {
            throw new Error('AgreementModal.fire: Missing required parameters');
        }

        // Only handle predict_module and clean_module
        if (!['predict_module', 'clean_module', 'audience_module'].includes(featureType)) {
            return Promise.resolve({ 
                isConfirmed: false,
                redirectPath: null,
                error: 'Invalid featureType'
            });
        }

        // time
        const today = moment.tz('America/Chicago').toDate();
        const firstBillingDate = moment.tz('America/Chicago').add(30, 'days').toDate();
        
        // module name
        const predictModuleName = global.betaFeature && global.betaFeature.predict_module && global.betaFeature.predict_module.name || 'Predict Module';
        const cleanModuleName = global.betaFeature && global.betaFeature.clean_module && global.betaFeature.clean_module.name || 'Clean Module';
        const audienceModuleName = global.betaFeature && global.betaFeature.audience_module && global.betaFeature.audience_module.name || 'Audience Module';
        
        // Check agreement status untuk predict, clean_id, dan audience_id
        // Data selalu dikirim dari main.js (dari backend getMarketingServicesAgreementFeature)
        if (!multiAgreementsData || typeof multiAgreementsData !== 'object') {
            throw new Error('AgreementModal.fire: multiAgreementsData is required');
        }

        // get agency sidebar
        const agencySidebar = global.agencysidebar || {};
        // console.log('agencySidebar', agencySidebar);
        const predictAgencyEnable = agencySidebar.predict === true;
        const cleanAgencyEnable = agencySidebar.clean === true; // Temporary default true
        const audienceAgencyEnable = agencySidebar.audience === true; // ini persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false

        // Get predict dan clean feature IDs
        const betaFeature = global.betaFeature || {};
        const predictFeatureId = betaFeature.predict_module && betaFeature.predict_module.id || null;
        const cleanFeatureId = betaFeature.clean_module && betaFeature.clean_module.id || null;
        const audienceFeatureId = betaFeature.audience_module && betaFeature.audience_module.id || null;
        
        // Check apakah ada yang belum agree
        const predictHasAgreement = multiAgreementsData[predictFeatureId] === 'T';
        const cleanHasAgreement = multiAgreementsData[cleanFeatureId] === 'T';
        const audienceHasAgreement = multiAgreementsData[audienceFeatureId] === 'T'; // audience module belum di develop, jadi jangan dianggap agree, pasti hasilnya false
        
        // Ambil pricing info jika ada
        const pricing = passedPricing || {};
        
        // Hanya tambahkan module yang enable di agency sidebar dan belum ada agreement
        const modules = [];
        if (predictAgencyEnable && !predictHasAgreement) {
            const predictPricing = pricing.predict || {};
            const monthlyFee = predictPricing.PredictMinCostMonth || 99.00;
            const monthlyAllocation = predictPricing.PredictMaxCampaigns ? (predictPricing.PredictMaxCampaigns + ' campaigns') : '50 campaigns';
            const overageRate = predictPricing.PredictOverageRate || 2.50;
            const annualCommitmentTotal = monthlyFee * 12;
            
            modules.push({
                name: predictModuleName,
                monthlyFee: monthlyFee,
                monthlyAllocation: monthlyAllocation,
                overageBilling: true,
                overageRate: overageRate,
                commitmentTerm: 12,
                billingFrequency: 'Monthly in advance',
                effectiveDate: today,
                firstBillingDate: firstBillingDate,
                annualCommitmentTotal: annualCommitmentTotal
            });
        }
        if (cleanAgencyEnable && !cleanHasAgreement) {
            // Clean ID menggunakan dummy/default seperti awal
            modules.push({
                name: cleanModuleName,
                monthlyFee: 149.00,
                monthlyAllocation: '100 contacts',
                overageBilling: true,
                overageRate: 3.00,
                commitmentTerm: 12,
                billingFrequency: 'Monthly in advance',
                effectiveDate: today,
                firstBillingDate: firstBillingDate,
                annualCommitmentTotal: 1788.00
            });
        }
        if (audienceAgencyEnable && !audienceHasAgreement) {
            modules.push({
                name: audienceModuleName,
                monthlyFee: 149.00,
                monthlyAllocation: '100 contacts',
                overageBilling: true,
                overageRate: 3.00,
                commitmentTerm: 12,
                billingFrequency: 'Monthly in advance',
                effectiveDate: today,
                firstBillingDate: firstBillingDate,
                annualCommitmentTotal: 1788.00
            });
        }
        
        // Jika semua module sudah ada agreement, langsung return dengan redirectPath
        // console.log('Jika semua module sudah ada agreement, langsung return dengan redirectPath', {
        //     'modules': modules
        // })
        if (modules.length === 0) {
            const redirectPath = this.getRedirectPath({ 
                featureType, 
                global, 
                to,
                from,
                selectedModules: [] 
            });
            return Promise.resolve({ 
                isConfirmed: true,
                redirectPath: redirectPath || (from ? from.fullPath : null),
                selectedModules: [],
                hasAgreement: true
            });
        }

        // Return Promise yang akan resolve/reject berdasarkan user action
        return new Promise((resolve, reject) => {
            // Create Vue instance untuk modal component
            const ModalComponent = Vue.extend(SubscriptionConfirmationModal);
            
            // Get router dari store atau parameter
            const routerInstance = router || (store._vm && store._vm.$router ? store._vm.$router : null);
            
            // Create instance dengan props dan event handlers
            // Hanya modules yang perlu di-pass sebagai prop
            const propsData = {
                modules: modules
            };
            const modalInstance = new ModalComponent({
                propsData: propsData,
                // Inject store dan router ke component instance
                store: store,
                router: routerInstance,
                // Inject parent untuk akses ke $global, $notify, dll
                parent: store._vm || null
            });

            // Setup event handlers ketika confirem
            modalInstance.$on('confirm', async (selectedModules) => {
                try {
                    // console.log('confirm: selectedModules', { selectedModules })
                    // Set loading state
                    modalInstance.internalIsLoading = true;

                    // Save agreements dan update client
                    // console.log('saveAgreements 1', { selectedModules });
                    await this.saveAgreements({ selectedModules, modules, featureType, store, global, userData, to });
                    // console.log('saveAgreements 2', { selectedModules });

                    // Reload beta feature data
                    // console.log('reloadBetaFeatureData 1', { selectedModules });
                    await this.reloadBetaFeatureData({ store, global, userData });
                    // console.log('reloadBetaFeatureData 2', { selectedModules });

                    // Tentukan redirect path berdasarkan module yang dipilih
                    // console.log('getRedirectPath 1', { selectedModules })
                    const redirectPath = this.getRedirectPath({ featureType, global, to, from, selectedModules });
                    // console.log('getRedirectPath 2', { selectedModules })

                    // Close modal - langsung set internalShow
                    modalInstance.internalShow = false;
                    setTimeout(() => {
                        modalInstance.$destroy();
                        if (modalInstance.$el && modalInstance.$el.parentNode) {
                            modalInstance.$el.parentNode.removeChild(modalInstance.$el);
                        }
                    }, 300);

                    // notification success
                    Vue.prototype.$notify({
                        type: 'success',
                        message: 'Agreement saved successfully.',
                        icon: 'fas fa-check-circle'
                    });

                    // Resolve dengan result
                    resolve({ 
                        isConfirmed: true, 
                        redirectPath: redirectPath,
                        selectedModules: selectedModules,
                        hasAgreement: false
                    });
                } catch (error) {
                    // Show error notification jika Vue tersedia
                    const errorMessage = (error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Something went wrong, please try again later';
                    if (typeof Vue !== 'undefined' && Vue.prototype.$notify) {
                        Vue.prototype.$notify({
                            type: 'danger',
                            message: errorMessage,
                            icon: 'fas fa-exclamation-triangle'
                        });
                    }
                    console.error('AgreementModal error:', error);
                    
                    // Reset loading state - modal tetap terbuka (tidak di-close)
                    modalInstance.internalIsLoading = false;
                }
            });
            // Setup event handlers ketika cancel
            modalInstance.$on('cancel', () => {
                // Close modal - gunakan computed property, bukan mutate prop langsung
                modalInstance.internalShow = false;
                setTimeout(() => {
                    modalInstance.$destroy();
                    if (modalInstance.$el && modalInstance.$el.parentNode) {
                        modalInstance.$el.parentNode.removeChild(modalInstance.$el);
                    }
                }, 300);

                // Reject dengan fromPath untuk redirect back
                reject({ 
                    isConfirmed: false,
                    redirectPath: from ? from.fullPath : null,
                    selectedModules: []
                });
            });

            // Mount component to body dengan z-index tinggi
            modalInstance.$mount();
            
            // Pastikan modal memiliki z-index tinggi
            if (modalInstance.$el) {
                modalInstance.$el.style.zIndex = '9999';
            }
            
            document.body.appendChild(modalInstance.$el);

            // Tunggu nextTick, baru trigger animation
            // Gunakan Vue.nextTick global untuk memastikan DOM sudah ready
            Vue.nextTick(() => {
                // Update internal state (internalShow) untuk menghindari Vue warning
                // Ini lebih aman daripada mutate prop langsung
                // internalShow sudah cukup, tidak perlu update propsData.show (redundant)
                modalInstance.internalShow = true;
                modalInstance.$forceUpdate();
            });
        });
    }

    /**
     * Tentukan redirect path berdasarkan module yang dipilih
     */
    static getRedirectPath({ featureType, global, to, from, selectedModules }) {
        console.log('getRedirectPath: options', { featureType, global, to, from, selectedModules })
        // Jika tidak ada module yang dipilih, gunakan fromPath
        if (!selectedModules || selectedModules.length === 0) {
            return from ? from.fullPath : null;
        }

        // get agency sidebar
        const agencySidebar = global.agencysidebar || {};
        const predictAgencyEnable = agencySidebar.predict === true;
        const cleanAgencyEnable = agencySidebar.clean === true;
        const audienceAgencyEnable = agencySidebar.audience === true; // ini persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false

        // Cek module yang dipilih
        const betaFeature = global.betaFeature || {};
        const selectedModuleNames = selectedModules.map(m => m.name);
        const hasPredictSelected = selectedModuleNames.some(name => name === (betaFeature.predict_module && betaFeature.predict_module.name) || name === 'Predict Module');
        const hasCleanSelected = selectedModuleNames.some(name => name === (betaFeature.clean_module && betaFeature.clean_module.name) || name === 'Clean Module');
        const hasAudienceSelected = selectedModuleNames.some(name => name === (betaFeature.audience_module && betaFeature.audience_module.name) || name === 'Audience Module');

        // url
        const predictUrl = global.globalModulNameLink && global.globalModulNameLink.predict && global.globalModulNameLink.predict.url;
        const cleanUrl = global.globalModulNameLink && global.globalModulNameLink.clean && global.globalModulNameLink.clean.url || 'cleanid';
        const audienceUrl = global.globalModulNameLink && global.globalModulNameLink.audience && global.globalModulNameLink.audience.url || 'audienceid'; // ini untuk persiapan saja
        
        // predict priority ke-1
        if (predictAgencyEnable && hasPredictSelected && predictUrl) { 
            return '/' + encodeURIComponent(predictUrl) + '/campaign-management';
        } 

        // predict priority ke-2
        if (cleanAgencyEnable && hasCleanSelected && cleanUrl) { 
            return '/' + encodeURIComponent(cleanUrl) + '/';
        } 
        
         // 3. Jika hanya audience dipilih
        if (audienceAgencyEnable && hasAudienceSelected && audienceUrl) {
            return '/' + encodeURIComponent(audienceUrl) + '/campaign-management';
        }

        // 4. Fallback: gunakan toPath dari router guard
        return to ? to.path : null;
    }

    /**
     * Save agreements dan update client
     */
    static async saveAgreements({ selectedModules, modules, featureType, store, global, userData, to }) {
        const selectedModulesMap = {};
        selectedModules.forEach(module => {
            selectedModulesMap[module.name] = true;
        });

        // Get existing SelectedModules dari global.agencysidebar
        const agencySidebar = global.agencysidebar || {};
        const existingSelectedModules = Object.keys(agencySidebar).map(key => ({
            type: key,
            status: agencySidebar[key] === true
        }));
        
        // Build selectedModulesArray
        const betaFeature = global.betaFeature || {};

        const predictModuleName = betaFeature.predict_module && betaFeature.predict_module.name || 'Predict Module';
        const cleanModuleName = betaFeature.clean_module && betaFeature.clean_module.name || 'Clean Module';
        const audienceModuleName = betaFeature.audience_module && betaFeature.audience_module.name || 'Audience Module';
        
        const hasPredictInAvailableModules = modules.some(module => module.name === predictModuleName || module.name === 'Predict Module');
        const hasCleanInAvailableModules = modules.some(module => module.name === cleanModuleName || module.name === 'Clean Module');
        const hasAudienceInAvailableModules = modules.some(module => module.name === audienceModuleName || module.name === 'Audience Module');
        
        let predictSelectedStatus = false;
        let cleanSelectedStatus = false;
        let audienceSelectedStatus = false;
        
        // Check dengan module name yang benar (bisa dari betaFeature atau fallback)
        if (hasPredictInAvailableModules) {
            predictSelectedStatus = selectedModulesMap[predictModuleName] || selectedModulesMap['Predict Module'] || false;
        }
        if (hasCleanInAvailableModules) {
            cleanSelectedStatus = selectedModulesMap[cleanModuleName] || selectedModulesMap['Clean Module'] || false;
        }
        if (hasAudienceInAvailableModules) {
            audienceSelectedStatus = selectedModulesMap[audienceModuleName] || selectedModulesMap['Audience Module'] || false;
        }
        
        const selectedModulesArray = [];
        existingSelectedModules.forEach(module => {
            if (module.type === 'predict' && hasPredictInAvailableModules) {
                selectedModulesArray.push({
                    type: 'predict',
                    status: predictSelectedStatus
                });
            } else if (module.type === 'clean' && hasCleanInAvailableModules) { // ini hanya persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false
                selectedModulesArray.push({
                    type: 'clean',
                    status: cleanSelectedStatus
                });
            } else if (module.type === 'audience' && hasAudienceInAvailableModules) { // ini hanya persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false
                selectedModulesArray.push({
                    type: 'audience',
                    status: audienceSelectedStatus
                });
            } else {
                selectedModulesArray.push({
                    type: module.type,
                    status: module.status
                });
            }
        });
        
        // Handle jika module tidak ada di existingSelectedModules tapi user memilih
        const hasPredictInExisting = existingSelectedModules.some(m => m.type === 'predict');
        if (!hasPredictInExisting && predictSelectedStatus) {
            selectedModulesArray.push({
                type: 'predict',
                status: predictSelectedStatus
            });
        }
        
        const hasCleanInExisting = existingSelectedModules.some(m => m.type === 'clean');
        if (!hasCleanInExisting && cleanSelectedStatus) { // ini hanya persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false
            selectedModulesArray.push({
                type: 'clean',
                status: cleanSelectedStatus
            });
        }

        const hasAudienceInExisting = existingSelectedModules.some(m => m.type === 'audience');
        if (!hasAudienceInExisting && audienceSelectedStatus) { // ini hanya persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false
            selectedModulesArray.push({
                type: 'audience',
                status: audienceSelectedStatus
            });
        }
        
        // Build modules payload for bulk agreement
        const predictModuleId = betaFeature.predict_module && betaFeature.predict_module.id || null;
        const cleanModuleId = betaFeature.clean_module && betaFeature.clean_module.id || null;
        const audienceModuleId = betaFeature.audience_module && betaFeature.audience_module.id || null;

        const modulesPayload = [];  
        for (const module of modules) {
            let moduleFeatureType = null;
            let moduleFeatureId = null;
            let moduleFeatureName = module.name;
            let isSelected = selectedModulesMap[module.name] || false;
            
            if (module.name === predictModuleName || module.name === 'Predict Module') {
                moduleFeatureType = 'predict_module';
                moduleFeatureId = predictModuleId;
            } else if (module.name === cleanModuleName || module.name === 'Clean Module') {
                moduleFeatureType = 'clean_module';
                moduleFeatureId = cleanModuleId;
            } else if (module.name === audienceModuleName || module.name === 'Audience Module') { // ini hanya persiapan saja, soalnya module nya belom di develop, dan hasilnya pasti false
                moduleFeatureType = 'audience_module';
                moduleFeatureId = audienceModuleId;
            }
            
            if (moduleFeatureType && moduleFeatureId && isSelected) {
                modulesPayload.push({
                    feature_id: moduleFeatureId,
                    feature_slug: moduleFeatureType,
                    feature_name: moduleFeatureName,
                });
            }
        }

        if (modulesPayload.length > 0) {
            try {
                const agreementResponse = await store.dispatch('marketingServicesAgreementFeatureMulti', {
                    user_id: userData.id,
                    company_id: userData.company_id,
                    company_name: userData.company_name,
                    location_menu: 'Campaign Management',
                    modules: modulesPayload,
                });

                // Check if response indicates error
                if (!agreementResponse || agreementResponse.result !== 'success') {
                    const errorMessage = (agreementResponse && agreementResponse.message) || 'Failed to save marketing services agreements. Please try again.';
                    throw new Error(errorMessage);
                }
            } catch (error) {
                // Re-throw error agar bisa di-handle di catch block parent (line 226)
                // Modal akan tetap terbuka dan tidak lanjut ke UpdateClient
                throw error;
            }
        }
        
        // Update client - hanya jalan kalau agreement berhasil
        await store.dispatch('UpdateClient', {
            companyID: userData.company_id,
            idsys: global.idsys,
            ClientID: userData.id,
            ClientCompanyName: userData.company_name,
            ClientFullName: userData.name || userData.full_name,
            ClientEmail: userData.email,
            ClientPhone: userData.phonenum || userData.phone || '',
            ClientPhoneCountryCode: userData.phone_country_code || '',
            ClientPhoneCountryCallingCode: userData.phone_country_calling_code || '',
            selectedmodules: selectedModulesArray,
            action: 'downline',
        });
    }

    /**
     * Reload beta feature data
     */
    static async reloadBetaFeatureData({ store, global, userData }) {
        if (userData.user_type === 'client') {
            await store.dispatch('checkUserSetupComplete', {
                usrID: userData.id,
            }).then(response => {
                global.isBeta = response.isBeta;
                global.betaFeature = response.betaFeature;
            });
        } else {
            console.log('reloadBetaFeatureData else');
            const companyID = userData.company_id;
            const roleid = userData.role_id;
            await store.dispatch('GetRoleList', {
                companyID: companyID,
                getType: 'getrolemodule',
                roleID: roleid,
                usrID: userData.id,
            }).then(response => {
                global.isBeta = response.isBeta;
                global.betaFeature = response.betaFeature;
                
                // Update agency_side_menu juga
                global.agency_side_menu = response.agency_side_menu || [];
                if (global.agency_side_menu && global.agency_side_menu.length > 0) {
                    // Inisialisasi agencysidebar jika belum ada
                    if (!global.agencysidebar) {
                        global.agencysidebar = {};
                    }
                    
                    // Update status untuk setiap menu type berdasarkan agency_side_menu
                    const menuTypes = ['local', 'locator', 'enhance', 'b2b', 'simplifi', 'predict', 'clean', 'audience'];
                    menuTypes.forEach(menuType => {
                        const menuItem = global.agency_side_menu.find(item => item.type === menuType);
                        if (menuItem) {
                            global.agencysidebar[menuType] = menuItem.status === true || menuItem.status === 'T';
                        } else {
                            // Jika tidak ada di agency_side_menu, set ke false
                            global.agencysidebar[menuType] = false;
                        }
                    });
                }
            });
        }
    }
}

export default AgreementModal;
